import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, ArrowRight, Check } from '@/components/icons';
import { BROWSERS, PLATFORMS, type Browser, type Platform } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { URL_HELP, URL_RULE_MESSAGE, isValidUrl } from '@/lib/format';
import { Button, Dialog, Field, Input, Select, Textarea, useToast } from '@/components/ui';
import { PLATFORM_META, PlatformIcon } from './PlatformIcon';

const BROWSER_LABELS: Record<Browser, string> = { chromium: 'Chrome (Chromium)', firefox: 'Firefox', webkit: 'Safari (WebKit)' };

const STEPS = ['Platform', 'App details', 'Name'] as const;

export function NewProjectWizard({ orgId, open, onClose }: { orgId: string; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [platform, setPlatform] = useState<Platform>('WEB');
  const [targetUrl, setTargetUrl] = useState('https://');
  const [appId, setAppId] = useState('');
  const [browser, setBrowser] = useState<Browser>('chromium');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const reset = () => {
    setStep(0);
    setPlatform('WEB');
    setTargetUrl('https://');
    setAppId('');
    setBrowser('chromium');
    setName('');
    setDescription('');
  };

  const close = () => {
    reset();
    onClose();
  };

  // Web, performance and security projects have an address (the app under test / to load).
  const hasUrl = platform === 'WEB' || platform === 'PERFORMANCE' || platform === 'SECURITY';
  const create = useMutation({
    mutationFn: () =>
      api.createProject(orgId, {
        name: name.trim(),
        description: description.trim() || undefined,
        platform,
        targetUrl: hasUrl ? targetUrl.trim() : undefined,
        appId: !hasUrl ? appId.trim() : undefined,
        settings: platform === 'WEB' ? { browser } : {},
      }),
    onSuccess: (p) => {
      toast.success(`Project “${p.name}” created`);
      qc.invalidateQueries({ queryKey: ['projects', orgId] });
      close();
      // Performance projects start with a script; security projects set their scope then add a scan;
      // automation projects start with a test.
      navigate(
        p.platform === 'PERFORMANCE'
          ? `/projects/${p.id}?tab=scripts`
          : p.platform === 'SECURITY'
            ? `/projects/${p.id}/security/new`
            : `/projects/${p.id}/tests/new`,
      );
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const detailsValid =
    platform === 'WEB' || platform === 'SECURITY'
      ? isValidUrl(targetUrl.trim())
      : platform === 'PERFORMANCE'
        ? /^https?:\/\/[^/\s]+/i.test(targetUrl.trim())
        : appId.trim().length > 2;
  const canNext = step === 0 || (step === 1 && detailsValid) || (step === 2 && name.trim().length > 0);

  const next = () => {
    if (!canNext) return;
    if (step < STEPS.length - 1) {
      if (step === 1 && !name && hasUrl) {
        try {
          setName(new URL(targetUrl).hostname.replace(/^www\./, ''));
        } catch {
          /* ignore */
        }
      }
      setStep(step + 1);
    } else create.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      size="lg"
      title="New test project"
      description="A project holds the tests for one app: automation (web, Android, iOS), performance or security."
      footer={
        <>
          {step > 0 && (
            <Button variant="ghost" onClick={() => setStep(step - 1)} className="mr-auto" icon={<ArrowLeft className="size-4" />}>
              Back
            </Button>
          )}
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={next} disabled={!canNext} loading={create.isPending}>
            {step === STEPS.length - 1 ? 'Create project' : 'Continue'}
            {step < STEPS.length - 1 && <ArrowRight className="size-4" />}
          </Button>
        </>
      }
    >
      <ol className="mb-6 flex items-center gap-2" aria-label="Progress">
        {STEPS.map((label, i) => (
          <li key={label} className="flex flex-1 items-center gap-2" aria-current={i === step ? 'step' : undefined}>
            <span
              className={clsx(
                'grid size-7 shrink-0 place-items-center rounded-full font-display text-xs transition',
                i < step && 'bg-success text-background',
                i === step && 'bg-primary text-primary-fg',
                i > step && 'bg-surface-raised text-fg-muted',
              )}
            >
              {i < step ? <Check className="size-3" /> : String(i + 1).padStart(2, '0')}
            </span>
            <span className={clsx('text-xs font-semibold tracking-label whitespace-nowrap uppercase', i === step ? 'text-fg' : 'text-fg-muted')}>{label}</span>
            {i < STEPS.length - 1 && <span className={clsx('h-px flex-1', i < step ? 'bg-success' : 'bg-border')} />}
          </li>
        ))}
      </ol>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          next();
        }}
      >
        {step === 0 && (
          <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Platform">
            {PLATFORMS.map((p) => {
              const m = PLATFORM_META[p];
              const selected = platform === p;
              return (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setPlatform(p)}
                  className={clsx(
                    'flex flex-col items-start gap-3 rounded-card p-4 text-left ring-1 ring-inset transition',
                    selected ? 'bg-primary-subtle/40 ring-primary' : 'bg-surface ring-border hover:ring-fg-muted',
                  )}
                >
                  <PlatformIcon platform={p} />
                  <span>
                    <span className="block font-semibold text-fg">{m.label}</span>
                    <span className="mt-0.5 block text-xs text-fg-muted">{m.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {step === 1 && platform === 'WEB' && (
          <div className="space-y-5">
            <Field
              label="Website address"
              hint={URL_HELP}
              error={targetUrl.length > 8 && !isValidUrl(targetUrl.trim()) ? URL_RULE_MESSAGE : undefined}
            >
              {(id) => <Input id={id} autoFocus type="url" placeholder="https://" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} />}
            </Field>
            <Field label="Browser" hint="You can change this later.">
              {(id) => (
                <Select
                  id={id}
                  value={browser}
                  onChange={(e) => setBrowser(e.target.value as Browser)}
                  options={BROWSERS.map((b) => ({ value: b, label: BROWSER_LABELS[b] }))}
                />
              )}
            </Field>
          </div>
        )}

        {step === 1 && platform === 'PERFORMANCE' && (
          <Field label="Address of the app to load" hint="The address performance tests put load on (also an internal test server, e.g. http://staging.local). Scripts may only reach this host and the hosts a Project Admin allows.">
            {(id) => <Input id={id} autoFocus type="url" placeholder="https://" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} />}
          </Field>
        )}

        {step === 1 && platform === 'SECURITY' && (
          <Field
            label="Address of the app to test"
            hint="The app the pentester scans. Use a staging or test address — you confirm the authorized hosts (scope) on the project before any scan runs."
            error={targetUrl.length > 8 && !isValidUrl(targetUrl.trim()) ? URL_RULE_MESSAGE : undefined}
          >
            {(id) => <Input id={id} autoFocus type="url" placeholder="https://" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} />}
          </Field>
        )}

        {step === 1 && (platform === 'ANDROID' || platform === 'IOS') && (
          <Field
            label={platform === 'ANDROID' ? 'Android package name' : 'iOS bundle identifier'}
            hint={
              platform === 'ANDROID'
                ? 'Find it in your Play Store link, e.g. com.mycompany.app. Your developer can upload the app build later.'
                : 'For example com.mycompany.app. Your developer can upload the simulator build later.'
            }
          >
            {(id) => <Input id={id} autoFocus placeholder="com.mycompany.app" value={appId} onChange={(e) => setAppId(e.target.value)} />}
          </Field>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <Field label="Project name">
              {(id) => <Input id={id} autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="My shop - checkout" maxLength={80} />}
            </Field>
            <Field label="Description" hint="Optional - what is this app and what should be tested?">
              {(id) => <Textarea id={id} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />}
            </Field>
          </div>
        )}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
